import { useTanstackSpotsFetchFn } from '@/components/Functional/hooks-core/useTanstackFetchFn'
import { haversineDistance } from '@/components/Functional/utils/calcGeoLocationDistance'
import type { SpotData } from '@/schemas/v2/spot'
import { useTokenStore } from '@/store/useTokenStore'
import { useQueryClient } from '@tanstack/react-query'
import { useMachine } from '@xstate/react'
import { useEffect } from 'react'
import { assign, fromPromise, setup } from 'xstate'

/** ダイアログに表示するスポット候補 */
export type NearbySpotItem = {
  spotId: number
  spotName: string
  address: string | null
  /** GPS 距離（km）。null = GPS 取得不可フォールバック */
  distanceKm: number | null
  /** スタンプ獲得済みか（認証済み時のみ設定） */
  isAcquired: boolean
  /** スポット代表画像 URL（images[0]）。未設定なら null */
  imageUrl: string | null
}

type ApplicationTriggerContext = {
  isAuthenticated: boolean
  isAuthInitialized: boolean
  spots: SpotData[]
  gps: {
    lat: number
    lon: number
    available: boolean
  }
  nearbySpots: NearbySpotItem[]
  selectedSpotId: number | null
  errorMessage: string | null
}

type ApplicationTriggerEvent =
  | { type: 'AUTH_READY'; isAuthenticated: boolean }
  | { type: 'TRIGGER' }
  | { type: 'SPOT_SELECTED'; spotId: number }
  | { type: 'DIALOG_CLOSE' }
  | { type: 'RESET' }

type GPSResult = { lat: number; lon: number; available: boolean }

const getGPS = (): Promise<GPSResult> =>
  new Promise<GPSResult>((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ lat: 0, lon: 0, available: false })
      return
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ lat: coords.latitude, lon: coords.longitude, available: true }),
      () => resolve({ lat: 0, lon: 0, available: false }),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    )
  })

const applicationTriggerMachineBase = setup({
  types: {
    context: {} as ApplicationTriggerContext,
    events: {} as ApplicationTriggerEvent,
    input: {} as { isAuthenticated: boolean; isAuthInitialized: boolean },
  },
  actors: {
    fetchGPS: fromPromise<GPSResult, void>(async () => ({ lat: 0, lon: 0, available: false })),
    fetchSpots: fromPromise<SpotData[], void>(async () => []),
  },
}).createMachine({
  id: 'applicationTriggerFlow',
  initial: 'idle',
  context: ({ input }) => ({
    isAuthenticated: input.isAuthenticated,
    isAuthInitialized: input.isAuthInitialized,
    spots: [],
    gps: { lat: 0, lon: 0, available: false },
    nearbySpots: [],
    selectedSpotId: null,
    errorMessage: null,
  }),
  states: {
    idle: {
      on: { TRIGGER: 'preparing' },
    },
    preparing: {
      type: 'parallel',
      states: {
        gpsLocating: {
          initial: 'fetching',
          states: {
            fetching: {
              invoke: {
                src: 'fetchGPS',
                onDone: {
                  target: 'done',
                  actions: assign({ gps: ({ event }) => event.output }),
                },
                onError: {
                  target: 'done',
                  actions: assign({ gps: () => ({ lat: 0, lon: 0, available: false }) }),
                },
              },
            },
            done: { type: 'final' },
          },
        },
        spotsFetching: {
          initial: 'fetching',
          states: {
            fetching: {
              invoke: {
                src: 'fetchSpots',
                onDone: {
                  target: 'done',
                  actions: assign({ spots: ({ event }) => event.output }),
                },
                onError: { target: '#applicationTriggerFlow.error' },
              },
            },
            done: { type: 'final' },
          },
        },
      },
      onDone: 'sortingSpots',
    },
    sortingSpots: {
      always: [
        {
          target: 'selectingSpot',
          actions: assign({
            nearbySpots: ({ context }) => buildNearbySpots(context),
          }),
        },
      ],
    },
    selectingSpot: {
      on: {
        SPOT_SELECTED: {
          target: 'spotSelected',
          actions: assign({ selectedSpotId: ({ event }) => event.spotId }),
        },
        DIALOG_CLOSE: 'idle',
      },
    },
    spotSelected: {
      always: 'idle',
    },
    error: {
      on: { RESET: 'idle', TRIGGER: 'idle' },
    },
  },
})

function buildNearbySpots(context: ApplicationTriggerContext): NearbySpotItem[] {
  const MAX_DISPLAY = 3

  // 現在押印可能なスタンプを持つスポットのみを表示対象とする
  const stampableSpots = context.spots.filter((s) => s.hasStamp)

  const candidates = stampableSpots.map((s) => {
    const hasCoords = s.latitude != null && s.longitude != null
    const distanceKm =
      context.gps.available && hasCoords
        ? haversineDistance(
            { latitude: context.gps.lat, longitude: context.gps.lon },
            { latitude: s.latitude as number, longitude: s.longitude as number },
            'km',
          )
        : null
    return { spot: s, distanceKm }
  })

  const sorted = context.gps.available
    ? candidates.sort((a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0))
    : candidates.sort((a, b) => a.spot.id - b.spot.id)

  return sorted.slice(0, MAX_DISPLAY).map(({ spot, distanceKm }) => ({
    spotId: spot.id,
    spotName: spot.name,
    address: spot.address ?? null,
    distanceKm,
    isAcquired: false,
    imageUrl: spot.images[0] ?? null,
  }))
}

export function useApplicationTriggerFlow() {
  const queryClient = useQueryClient()
  const spotsFetchFn = useTanstackSpotsFetchFn()
  const idToken = useTokenStore((s) => s.idToken)
  const tokenLoading = useTokenStore((s) => s.loading)

  const [state, send] = useMachine(
    applicationTriggerMachineBase.provide({
      actors: {
        fetchGPS: fromPromise(async () => getGPS()),
        fetchSpots: fromPromise(async () => {
          const res = await queryClient.fetchQuery(spotsFetchFn)
          return res?.spots ?? []
        }),
      },
    }),
    {
      input: (() => {
        const tokenState = useTokenStore.getState()
        return {
          isAuthenticated: !tokenState.loading && tokenState.idToken !== null,
          isAuthInitialized: !tokenState.loading,
        }
      })(),
    },
  )

  useEffect(() => {
    if (tokenLoading) return
    send({ type: 'AUTH_READY', isAuthenticated: idToken !== null })
  }, [tokenLoading, idToken, send])

  return {
    isSelectingSpot: state.matches('selectingSpot'),
    gpsAvailable: state.context.gps.available,
    nearbySpots: state.context.nearbySpots,
    trigger: () => send({ type: 'TRIGGER' }),
    selectSpot: (spotId: number) => send({ type: 'SPOT_SELECTED', spotId }),
    closeDialog: () => send({ type: 'DIALOG_CLOSE' }),
  }
}
